import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, Fragment, useState, } from 'react';
import { Hidden, Drawer, Divider, List, ListItem, ListItemText, Box, makeStyles, Icon, Collapse, } from '@material-ui/core';
import clsx from 'clsx';
import produce from 'immer';
import { FontIcon, } from '.';
const useStyles = (optimizeForMediumScreen) => {
    return makeStyles((theme) => {
        const mediumScreenStyle = {
            root: {
                width: 65,
            },
            drawerPaper: {
                width: 65,
            },
            divider: {
                margin: theme.spacing(0),
                '&.items-divider': {
                    margin: theme.spacing(1, 0),
                },
            },
            appLogo: {
                '& .app-title': {
                    display: 'none',
                },
            },
            menuItem: {
                '& .menu-item-wrapper': {
                    justifyContent: 'center',
                    '& .icon': {
                        marginRight: theme.spacing(0),
                    },
                    '& .label-container': {
                        display: 'none',
                    },
                    '& .toggle-icon': {
                        display: 'none',
                    },
                },
            },
            subMenuItem: {
                '& .menu-item-wrapper': {
                    padding: theme.spacing(0.3, 1),
                    '& .icon': {
                        marginRight: theme.spacing(0),
                    },
                },
            },
        };
        return {
            root: {
                flexShrink: 0,
                width: 250,
            },
            divider: {
                margin: theme.spacing(0, 2),
                '&.items-divider': {
                    margin: theme.spacing(1, 2),
                },
            },
            drawerContentContainer: {
                flex: 1,
                position: 'relative',
                zIndex: 2,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            },
            toolbar: theme.mixins.toolbar,
            appLogo: {
                padding: theme.spacing(2, 1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                '& .logo-icon': {
                    flexShrink: 0,
                    height: 30,
                    fontSize: 30,
                    marginRight: theme.spacing(1),
                    [theme.breakpoints.up('sm')]: {
                        height: 35,
                        fontSize: 35,
                    },
                    [theme.breakpoints.only('md')]: {
                        marginRight: 0,
                    },
                    '&.wrapper': {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '& > *': {
                            height: '100%',
                            fontSize: '1em',
                        },
                    },
                },
                '& .app-title': {
                    fontWeight: 'bold',
                    fontSize: '16px',
                    maxWidth: '100%',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                },
            },
            drawerPaper: {
                width: 250,
                backgroundPosition: 'center center',
                backgroundSize: 'cover',
                '&::before': {
                    content: '""',
                    width: '100%',
                    height: '100%',
                    position: 'absolute',
                    zIndex: 1,
                    top: 0,
                    left: 0,
                    background: 'rgba(255, 255, 255, 0.8)',
                },
            },
            content: {
                flexGrow: 1,
            },
            menuItemList: {
                overflow: 'hidden auto',
                flex: 1,
            },
            menuItem: {
                padding: theme.spacing(0, 1),
                '& .menu-item-wrapper': {
                    width: '100%',
                    borderRadius: 3,
                    padding: theme.spacing(0.5, 1),
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: 40,
                    '& .icon': {
                        marginRight: theme.spacing(2),
                        fontSize: 24,
                        width: 24,
                        height: 24,
                        '&.wrapper': {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            '& > *': {
                                height: '100%',
                                fontSize: '1em',
                            },
                        },
                    },
                    '& .label-container': {
                        display: 'block',
                    },
                },
                '&.disabled': {
                    pointerEvents: 'none',
                    opacity: 0.2,
                },
            },
            subMenuItemsContainer: {
                marginBottom: theme.spacing(1),
            },
            subMenuItem: {
                '& .menu-item-wrapper': {
                    minHeight: 30,
                    padding: theme.spacing(0.3, 1),
                    paddingLeft: theme.spacing(6),
                    '& .icon': {
                        marginRight: theme.spacing(1),
                        fontSize: 20,
                        width: 20,
                        height: 20,
                    },
                    '& .label': {
                        fontSize: '0.95em',
                    },
                },
            },
            projectNameSection: {
                display: 'block',
                [theme.breakpoints.up('sm')]: {
                    display: 'none',
                },
            },
            projectName: {
                fontWeight: 'bold',
                padding: theme.spacing(1),
                fontSize: '18px',
                width: '100%',
                textAlign: 'center',
                [theme.breakpoints.up('sm')]: {
                    fontSize: '20px',
                },
            },
            [theme.breakpoints.only('md')]: optimizeForMediumScreen ? mediumScreenStyle : {
                root: {
                    width: 0,
                },
            },
        };
    }, {
        name: 'DrawerMenu',
    });
};
/**
 * Custom hook to mark the menu items expanded when menuItems, expandAllOnLoad, or collapsible state changed.
 */
const useInitializeExpandedMenu = (menuGroups, expandAllOnLoad, collapsible) => {
    // Mark the menu as "expanded" if expandAllOnLoad or if collapsible is set to "false" because if "collapsible" is false then
    // there is no other way to expand the menus so we have to make the menus expanded.
    const expanded = expandAllOnLoad !== false || collapsible === false;
    return menuGroups.map((itemGroup) => {
        return itemGroup.map(() => {
            return expanded;
        });
    });
};
export const DrawerMenu = (props) => {
    const classes = useStyles(props.optimizeForMediumScreen !== false)();
    // The state to keep track of the expanded main menu. By default, all the main menu will automatically be expanded UNLESS
    // "expandAllOnLoad" is specifically set to false
    const [expandedMenus, setExpandedMenus] = useState(useInitializeExpandedMenu(props.menuItems, props.expandAllOnLoad, props.collapsible));
    const renderItemGroup = useCallback((itemGroup, groupIndex, isMainMenus, parentDisabled) => {
        return (_jsxs(Fragment, { children: [groupIndex > 0 && _jsx(Divider, { className: clsx(classes.divider, 'items-divider') }), itemGroup.filter((item) => {
                    return !item.hidden;
                }).map((item, itemIndex) => {
                    const hasSubmenu = item.subMenuItems && item.subMenuItems.length > 0;
                    return (_jsxs(Fragment, { children: [_jsx(ListItem, { className: clsx(classes.menuItem, !isMainMenus && classes.subMenuItem, (item.disabled || parentDisabled) && 'disabled'), selected: item.selected && !item.disabled && (!hasSubmenu || item.selectable === true), button: true, onClick: () => {
                                    // If this item DOES NOT have submenu OR it has submenu AND props.selectable is true then trigger
                                    // onMenuItemClicked.
                                    if (!hasSubmenu || item.selectable === true) {
                                        props.onMenuItemClicked(item.link);
                                    }
                                    // Only main menu will have toggler
                                    if (isMainMenus && props.collapsible !== false) {
                                        setExpandedMenus((currentState) => {
                                            return produce(currentState, (draft) => {
                                                if (!draft[groupIndex]) {
                                                    draft[groupIndex] = [];
                                                }
                                                draft[groupIndex][itemIndex] = !draft[groupIndex][itemIndex];
                                            });
                                        });
                                    }
                                }, children: _jsxs("div", { className: "menu-item-wrapper", children: [item.icon && typeof item.icon === 'string' && (_jsx(FontIcon, { className: "icon", iconName: item.icon })), item.icon && typeof item.icon !== 'string' && (_jsx("div", { className: "icon wrapper", children: item.icon })), _jsx(ListItemText, { className: "label-container", primary: item.label, primaryTypographyProps: {
                                                className: 'label',
                                            } }), item.subMenuItems && item.subMenuItems.length > 0 && props.collapsible !== false && (_jsx(Icon, { className: "toggle-icon", children: expandedMenus[groupIndex][itemIndex] ? 'expand_less' : 'expand_more' }))] }) }, `${item.link}-${itemIndex.toString()}`), item.subMenuItems && isMainMenus && (_jsx(Collapse, { className: classes.subMenuItemsContainer, in: expandedMenus[groupIndex][itemIndex], timeout: "auto", unmountOnExit: true, children: renderItemGroup(item.subMenuItems, 0, false, Boolean(item.disabled)) }))] }, `${item.link}-${itemIndex.toString()}`));
                })] }, `group-${groupIndex.toString()}`));
    }, [classes.divider, classes.menuItem, classes.subMenuItem, classes.subMenuItemsContainer, expandedMenus, props]);
    const renderDrawerContent = useCallback(() => {
        return (_jsxs("div", { className: classes.drawerContentContainer, children: [_jsx(Box, { className: classes.toolbar, position: "relative", children: _jsxs("div", { className: classes.appLogo, children: [props.appLogo && typeof props.appLogo === 'string' && _jsx(FontIcon, { className: "logo-icon", iconName: props.appLogo }), props.appLogo && typeof props.appLogo !== 'string' && _jsx("div", { className: "logo-icon wrapper", children: props.appLogo }), _jsx("span", { className: "app-title", children: props.appName })] }) }), _jsx(Divider, { className: classes.divider }), _jsx(List, { className: classes.menuItemList, children: props.menuItems.map((itemGroup, groupIndex) => {
                        return renderItemGroup(itemGroup, groupIndex, true, false);
                    }) }), props.projectName && (_jsxs("div", { className: classes.projectNameSection, children: [_jsx(Divider, { className: classes.divider }), _jsx("div", { className: classes.projectName, children: props.projectName })] }))] }));
    }, [classes.appLogo, classes.divider, classes.drawerContentContainer, classes.menuItemList, classes.projectName, classes.projectNameSection,
        classes.toolbar, props.appLogo, props.appName, props.menuItems, props.projectName, renderItemGroup]);
    return (_jsxs("nav", { className: clsx(props.className, classes.root, props.optimizeForMediumScreen !== false && classes.optimizeForMDScreen), style: props.style, children: [_jsx(Hidden, { smDown: props.optimizeForMediumScreen !== false ? true : undefined, mdDown: props.optimizeForMediumScreen === false ? true : undefined, implementation: "css", children: _jsx(Drawer, { classes: {
                        paper: classes.drawerPaper,
                    }, variant: "permanent", open: true, PaperProps: {
                        style: {
                            backgroundImage: props.backgroundImage ? `url(${props.backgroundImage})` : 'none',
                        },
                    }, children: renderDrawerContent() }) }), _jsx(Hidden, { mdUp: props.optimizeForMediumScreen !== false ? true : undefined, lgUp: props.optimizeForMediumScreen === false ? true : undefined, implementation: "css", children: _jsx(Drawer, { variant: "temporary", anchor: "left", open: props.open, onClose: props.onClose, classes: {
                        paper: classes.drawerPaper,
                    }, ModalProps: {
                        keepMounted: true,
                    }, PaperProps: {
                        style: {
                            backgroundImage: props.backgroundImage ? `url(${props.backgroundImage})` : 'none',
                        },
                    }, children: renderDrawerContent() }) })] }));
};
